import React from 'react'
import styles from './RouteRideList.scss'
import { Link } from 'react-router'
import { IntroWidget } from 'common/IntroWidget'
import { SearchableCard } from 'common/SearchableCard'
import { findAsYouType } from 'lib/findAsYouType'
import { apiv3 } from 'lib/apiv3'
import { TRouteItemContainer } from 'common/TRouteItem'
import { CTA } from 'common/styleguide/CTA'
import { CircleSpinner } from 'common/spinners/CircleSpinner'
import cardStyles from 'common/SearchableCard/SearchableCard.scss'
import { DEFAULT_OFFSET } from 'lib/loadAllPages'
import { Msg, commonMessages, commonCTAs } from 'lib/localization'
import p from 'prop-types'
import _ from 'lodash'
import { FormattedMessage as T } from 'react-intl'

const PER_PAGE = 30

export class RouteRideList extends React.PureComponent {
  static propTypes = {
    onDeleteResult: p.func.isRequired,
    onResults: p.func.isRequired
  }

  constructor (props) {
    super(props)

    this.state = {
      search: props.initialTRoutes,
      cachedSearch: props.initialTRoutes,
      keywords: '',
      searchPending: false,
      offset: DEFAULT_OFFSET
    }

    this._search = findAsYouType({
      searchFn: this.executeSearch,
      onResults: this.onResults
    })
  }

  executeSearch = (options) => {
    const { user } = this.props
    const { keywords } = this.state
    return apiv3.search(Object.assign({
      keywords,
      limit: PER_PAGE,
      userId: user.id,
      sortBy: 'rank DESC',
      types: ['routes', 'trips']
    }, options))
  }

  onKeywordChange = (e) => {
    const keywords = e.target.value
    if (this.props.initialTRoutes && keywords.length === 0) {
      // when the user clears out the search, reset the search results state
      // so that they don't see an outdated search query on the transition
      // from initialTRoutes => results
      this.setState({ keywords, offset: DEFAULT_OFFSET, search: this.state.cachedSearch })
    } else {
      this.setState({ keywords, offset: DEFAULT_OFFSET }, this._search)
    }
  }

  onResults = (nextSearch) => {
    this.props.onResults(nextSearch)
    if (nextSearch.params.mergeResults) {
      this.setState((prevState) => {
        const combinedResults = _.concat(prevState.search.results, nextSearch.results)
        return {
          search: Object.assign({}, prevState.search, { results: combinedResults }),
          searchPending: false
        }
      })
    } else {
      this.setState({
        search: nextSearch,
        searchPending: false
      })
    }
  }

  onDelete = (record, promise) => {
    const { user } = this.props

    this.props.onDeleteResult(record)

    // We need to fire off a new search, so we get all fresh data
    // These options mirror the default search in <Loadable /> in Dashboard.js
    promise.then(() => this._search(
      {
        userId: user.id,
        sortBy: 'created DESC',
        limit: 20,
        types: ['routes', 'trips']
      }
    ))
  }

  loadMore = () => {
    const { keywords } = this.state

    this.setState((state) => ({
      searchPending: true, offset: state.offset + PER_PAGE
    }), () => this._search({
      sortBy: `${keywords === '' ? 'created' : 'rank'} DESC`,
      offset: this.state.offset,
      limit: this.state.offset + PER_PAGE
    })
    )
  }

  render () {
    const { user, owned } = this.props
    const { search, cachedSearch, keywords, searchPending } = this.state
    const { results, results_count: totalCount } = search

    return (
      <SearchableCard
        keywords={keywords}
        onKeywordChange={this.onKeywordChange}>
        <div className={styles.RouteRideList}>
          {results.map(r => <TRouteItemContainer
            key={`${r.name + r.id}`}
            r={r}
            editable={owned}
            deletable={owned}
            onDelete={owned && this.onDelete} />)}
          {results.length === 0 &&
              (search.results === cachedSearch.results
                ? <IntroWidget large i='md-bicycle'>
                  <T id='RouteRideList.noRoutesOrRides' defaultMessage='No routes or rides to display' />
                </IntroWidget>
                : <IntroWidget large i='md-bicycle'>
                  <T
                    id='RouteRideList.noResults'
                    defaultMessage="We couldn't find any results for {keywords}"
                    values={{ keywords: search.params.keywords }}
                    description='A message for when a search returns no results' />
                </IntroWidget>)}
          {owned && results.length > 0 && !keywords && <div className={styles.links}>
            <Link to='/routes'><T id='RouteRideList.viewRoutes' defaultMessage='View Routes' /></Link>
            {' '}
            <Link to='/rides'><T id='RouteRideList.viewRides' defaultMessage='View Rides' /></Link>
            {' '}
            <Link href={`/users/${user.id}/bucket_list`}><T id='RouteRideList.viewBucketList' defaultMessage='View Bucket List' /></Link>
          </div>}
          {!owned && totalCount > results.length && totalCount > PER_PAGE &&
          <div className={cardStyles.resultsFooter}>
            <CTA inline secondary margins={false} disabled={searchPending} onClick={() => !searchPending && this.loadMore()}>
              {searchPending && <CircleSpinner size={14} />} <Msg m={commonCTAs.loadMore} />
            </CTA>
            <div style={{ paddingTop: '20px' }}>
              <span className={cardStyles.footerText}>
                <Msg m={commonMessages.showingResults} values={{ length: results.length, totalCount }} />
              </span>
            </div>
          </div>}
        </div>
      </SearchableCard>
    )
  }
}
